import { useCallback, useState } from 'react';

/**
 * Single source of truth for the user's currently-selected brand blueprint id,
 * persisted to localStorage so the choice survives reloads and is shared across
 * pages. All localStorage access is guarded (mirrors AppLayout's sidebar
 * persistence) so a disabled/throwing storage backend never crashes the app.
 */
export const SELECTED_BLUEPRINT_STORAGE_KEY = 'eskimi:selectedBlueprintId';

function readStoredBlueprintId(): string {
  try {
    return localStorage.getItem(SELECTED_BLUEPRINT_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

/**
 * Write-only, non-hook access to the shared selection — for callers (e.g. a
 * blueprint-creation handler) that need to make their write the sticky
 * selection without subscribing to the current value via the hook below.
 */
export function setSelectedBlueprintId(id: string): void {
  try {
    localStorage.setItem(SELECTED_BLUEPRINT_STORAGE_KEY, id);
  } catch {
    // ignore — storage unavailable
  }
}

/**
 * Returns `[id, setId, clear]`:
 * - `id`     the persisted blueprint id (empty string when none selected)
 * - `setId`  write a new id through to localStorage and state
 * - `clear`  remove the persisted id
 *
 * localStorage failures are swallowed — they never throw to the caller.
 */
export function useSelectedBlueprint(): [string, (id: string) => void, () => void] {
  const [id, setIdState] = useState<string>(readStoredBlueprintId);

  const setId = useCallback((next: string) => {
    setIdState(next);
    setSelectedBlueprintId(next);
  }, []);

  const clear = useCallback(() => {
    setIdState('');
    try {
      localStorage.removeItem(SELECTED_BLUEPRINT_STORAGE_KEY);
    } catch {
      // ignore — storage unavailable
    }
  }, []);

  return [id, setId, clear];
}

export default useSelectedBlueprint;
